import { useState, useEffect } from 'react';
import { Wrench, Link, ShieldAlert } from 'lucide-react';
import apiService from '../../services/api';
import inductionService from '../../services/induction';
import type { Numeric } from '@/utils/numeric';
import { toNum } from '@/utils/numeric';

interface EquipmentOption {
  id: number;
  name: string;
  equipment_type: string;
  make?: string;
  model?: string;
  can_tow_implements?: boolean;
  is_implement?: boolean;
  working_width_m?: Numeric;
  boom_width_m?: Numeric;
  current_hours?: Numeric;
  farm_id?: number | null;
  farm_name?: string | null;
  assigned_user_id?: number | null;
  assigned_user_name?: string | null;
}

interface JobEquipmentPickerProps {
  farmIds?: number[];
  /**
   * The people on the job. Gear issued to them floats to the top and says so:
   * somebody who has been handed the trimmer for the season is the one
   * expected to use it. It is a preference, not a filter — anything can still
   * be chosen.
   */
  assigneeIds?: number[];
  primaryId: number | null;
  implementIds: number[];
  workingWidth: number | null;
  onPrimaryChange: (id: number | null) => void;
  onImplementsChange: (ids: number[]) => void;
  onWorkingWidthChange: (width: number | null) => void;
}

function holderNote(e: EquipmentOption): string {
  return e.assigned_user_id != null ? ` — with ${e.assigned_user_name ?? 'someone'}` : '';
}

function JobEquipmentPicker({
  farmIds, assigneeIds, primaryId, implementIds, workingWidth,
  onPrimaryChange, onImplementsChange, onWorkingWidthChange,
}: JobEquipmentPickerProps) {
  const [allEquipment, setAllEquipment] = useState<EquipmentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  // Who on the job is not inducted on which machine. A hint, not a filter:
  // saving still goes through the server's gate, which offers to sign them
  // off or ask a supervisor — a machine greyed out here would leave the
  // planner no way to get there.
  const [notInducted, setNotInducted] = useState<Map<number, string[]>>(new Map());
  const assigneeKey = (assigneeIds ?? []).join(',');

  useEffect(() => {
    const ids = assigneeKey ? assigneeKey.split(',').map(Number) : [];
    if (!ids.length) { setNotInducted(new Map()); return; }
    let cancelled = false;
    inductionService.check(ids)
      .then(res => {
        if (cancelled) return;
        const map = new Map<number, string[]>();
        for (const g of res.gaps) {
          const names = map.get(g.equipment_id) ?? [];
          const who = g.name || 'Someone';
          if (!names.includes(who)) names.push(who);
          map.set(g.equipment_id, names);
        }
        setNotInducted(map);
      })
      .catch(() => { if (!cancelled) setNotInducted(new Map()); });
    return () => { cancelled = true; };
  }, [assigneeKey]);
  const inductionNote = (id: number) => {
    const names = notInducted.get(id);
    return names?.length ? ` — not inducted: ${names.join(', ')}` : '';
  };
  const selectedGaps = [primaryId, ...implementIds]
    .filter((id): id is number => id != null && (notInducted.get(id)?.length ?? 0) > 0)
    .map(id => ({ id, name: allEquipment.find(e => e.id === id)?.name ?? 'This machine', people: notInducted.get(id)! }));

  useEffect(() => {
    apiService.get('/equipment?status=active&limit=200')
      .then((res: any) => {
        // apiService.get() already unwraps response.data,
        // so res = { success: true, data: [...] }
        let items: EquipmentOption[] = [];
        if (res.success && Array.isArray(res.data)) {
          items = res.data;
        } else if (Array.isArray(res)) {
          items = res;
        }
        setAllEquipment(items);
      })
      .catch(err => {
        console.error('Equipment load error:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  const farmIdSet = new Set((farmIds ?? []).filter(n => Number.isFinite(n)));
  const restrictByFarm = !showAll && farmIdSet.size > 0;

  // Restrict to equipment on the targeted farm(s); equipment with no farm
  // assignment is treated as shared and stays visible, and so is anything
  // issued to somebody on the job — it travels with them, not with a farm.
  const visibleEquipment = restrictByFarm
    ? allEquipment.filter(e =>
        e.farm_id == null || farmIdSet.has(e.farm_id)
        || (e.assigned_user_id != null && (assigneeIds ?? []).includes(e.assigned_user_id)))
    : allEquipment;

  const crew = new Set(assigneeIds ?? []);
  const heldByCrew = (e: EquipmentOption) => e.assigned_user_id != null && crew.has(e.assigned_user_id);

  // Sort: gear issued to the crew first, then targeted farm(s), then
  // unassigned, then other farms.
  const sortByFarm = (a: EquipmentOption, b: EquipmentOption) => {
    const rank = (e: EquipmentOption) => {
      if (e.farm_id != null && farmIdSet.has(e.farm_id)) return 0;
      if (e.farm_id == null) return 1;
      return 2;
    };
    const crewFirst = Number(heldByCrew(b)) - Number(heldByCrew(a));
    if (crewFirst !== 0) return crewFirst;
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return (a.farm_name ?? '').localeCompare(b.farm_name ?? '') || a.name.localeCompare(b.name);
  };
  const sorted = [...visibleEquipment].sort(sortByFarm);

  // Split by is_implement flag
  const tractors = sorted.filter(e => !e.is_implement);
  const implements_ = sorted.filter(e => e.is_implement);

  // If the farm filter hides a previously-selected item, drop it from state.
  useEffect(() => {
    if (!restrictByFarm) return;
    const visibleIds = new Set(visibleEquipment.map(e => e.id));
    if (primaryId && !visibleIds.has(primaryId)) onPrimaryChange(null);
    const filteredImpls = implementIds.filter(id => visibleIds.has(id));
    if (filteredImpls.length !== implementIds.length) onImplementsChange(filteredImpls);
    // visibleEquipment is derived from allEquipment + farm filter; depending on
    // the inputs is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictByFarm, allEquipment, farmIds]);

  const handleImplementChange = (id: number) => {
    let next: number[];
    if (implementIds.includes(id)) {
      next = implementIds.filter(i => i !== id);
    } else {
      next = [...implementIds, id];
    }
    onImplementsChange(next);

    // Auto-set working width from first implement
    if (next.length > 0 && !workingWidth) {
      const impl = allEquipment.find(e => e.id === next[0]);
      if (impl) {
        const w = toNum(impl.working_width_m) ?? toNum(impl.boom_width_m);
        if (w) onWorkingWidthChange(w);
      }
    }
  };

  if (loading) return <p className="text-sm text-neutral-400">Loading equipment...</p>;

  const hiddenByFilter = farmIdSet.size > 0 && !showAll
    ? allEquipment.length - visibleEquipment.length
    : 0;

  return (
    <div className="space-y-4">
      {farmIdSet.size > 0 && (
        <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-xs">
          <span className="text-indigo-700">
            {showAll
              ? `Showing all equipment.`
              : `Showing equipment on the targeted farm${farmIdSet.size > 1 ? 's' : ''}${hiddenByFilter > 0 ? ` (${hiddenByFilter} hidden)` : ''}.`}
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer text-indigo-700">
            <input
              type="checkbox"
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
              className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-200"
            />
            Show all
          </label>
        </div>
      )}

      {/* Primary equipment (tractor/vehicle) — optional */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          <Wrench className="h-4 w-4 inline mr-1" />
          Machine, vehicle or tool (optional)
        </label>
        <select
          value={primaryId ?? ''}
          onChange={e => onPrimaryChange(e.target.value ? parseInt(e.target.value) : null)}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
        >
          <option value="">None</option>
          {tractors.map(eq => (
            <option key={eq.id} value={eq.id}>
              {eq.name}{eq.make ? ` (${eq.make}${eq.model ? ` ${eq.model}` : ''})` : ''}
              {eq.current_hours != null ? ` — ${eq.current_hours}h` : ''}
              {holderNote(eq)}
              {eq.farm_name && (farmIdSet.size === 0 || (eq.farm_id != null && !farmIdSet.has(eq.farm_id))) ? ` [${eq.farm_name}]` : ''}
              {inductionNote(eq.id)}
            </option>
          ))}
        </select>
        {tractors.length === 0 && allEquipment.length > 0 && (
          <p className="text-xs text-neutral-400 mt-1">
            No non-implement equipment found. All your equipment is marked as implements.
          </p>
        )}
        {allEquipment.length === 0 && (
          <p className="text-xs text-amber-500 mt-1">
            No active equipment found. Add equipment in the Equipment module first.
          </p>
        )}
      </div>

      {/* Implement — optional, independent of tractor */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          <Link className="h-4 w-4 inline mr-1" />
          Implement (optional)
        </label>
        {implements_.length === 0 ? (
          <p className="text-sm text-neutral-400">
            {allEquipment.length > 0
              ? 'No implements found. Mark equipment as "implement" in the Equipment module.'
              : 'No equipment available.'}
          </p>
        ) : (
          <div className="space-y-1 max-h-40 overflow-y-auto border border-neutral-200 rounded-lg p-2">
            {implements_.map(impl => (
              <label key={impl.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={implementIds.includes(impl.id)}
                  onChange={() => handleImplementChange(impl.id)}
                  className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200"
                />
                <span className="text-sm text-neutral-700">
                  {impl.name}
                  {impl.assigned_user_id != null && (
                    <span className="text-xs text-violet-600 ml-1">with {impl.assigned_user_name ?? 'someone'}</span>
                  )}
                  {notInducted.get(impl.id)?.length ? (
                    <span className="text-xs text-warning-700 ml-1">not inducted: {notInducted.get(impl.id)!.join(', ')}</span>
                  ) : null}
                  {impl.farm_name && (farmIdSet.size === 0 || (impl.farm_id != null && !farmIdSet.has(impl.farm_id))) ? (
                    <span className="text-xs text-neutral-400 ml-1">[{impl.farm_name}]</span>
                  ) : null}
                </span>
                {impl.equipment_type && (
                  <span className="text-xs text-neutral-400">{impl.equipment_type.replace(/_/g, ' ')}</span>
                )}
                {impl.working_width_m && (
                  <span className="text-xs text-indigo-500">{impl.working_width_m}m wide</span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {selectedGaps.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-warning-500/40 bg-warning-50 px-3 py-2 text-xs text-warning-700">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <div className="min-w-0 space-y-1 break-words">
            {selectedGaps.map(g => (
              <p key={g.id}>{g.people.join(', ')} {g.people.length === 1 ? 'is' : 'are'} not inducted on {g.name}.</p>
            ))}
            <p>When you save, a supervisor can sign them off, or the job is saved without them and a supervisor is asked.</p>
          </div>
        </div>
      )}

      {/* Working Width Override */}
      {implementIds.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Working Width (m)</label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={workingWidth ?? ''}
            onChange={e => onWorkingWidthChange(e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Auto from implement"
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-200"
          />
          <p className="text-xs text-neutral-400 mt-1">Used for GPS coverage calculation</p>
        </div>
      )}
    </div>
  );
}

export default JobEquipmentPicker;
